/**
 * The two writes behind "Send a single card", as testable logic.
 *
 * Extracted from SendOneCard.tsx for the reason outboundRules.ts and
 * touchActions.ts were: a rule that spends money should be provable without a
 * network, a credential or a browser. See `marketing/CLAUDE.md`, "Writes that
 * are not plain CRUD". This screen is one of six that predate that pattern
 * (atomic#12); it is migrated first because it buys postage.
 *
 * These are NOT data provider methods, deliberately. The provider is built
 * once outside React, so a method on it cannot be swapped per test -- which is
 * exactly what the injected `rpc` parameter below buys.
 */

/** plan_one_door: queue one card for one door. Reversible; spends nothing. */
export type PlanOneRpc = (args: {
  p_address_id: number;
  p_touch: string;
  p_vendor: string | null;
}) => Promise<{ data: unknown; error: { message: string } | null }>;

/** unplan_one: discard one queued card. Refuses anything already sent. */
export type UnplanOneRpc = (args: {
  p_mailing_id: number;
}) => Promise<{ data: unknown; error: { message: string } | null }>;

/** The real ones. Lazily imported so a test that never writes needs no client. */
export const planOneViaSupabase: PlanOneRpc = async (args) => {
  const { getSupabaseClient } = await import("../providers/supabase/supabase");
  return getSupabaseClient().rpc("plan_one_door", args);
};

export const unplanOneViaSupabase: UnplanOneRpc = async (args) => {
  const { getSupabaseClient } = await import("../providers/supabase/supabase");
  return getSupabaseClient().rpc("unplan_one", args);
};

export type PlannedCard = {
  mailingId: number;
  /** The door already had this touch queued, so nothing new was created. */
  already: boolean;
};

/**
 * Read plan_one_door's return value.
 *
 * PostgREST hands a single-row SETOF back as an array, but a scalar-returning
 * shape as a bare object, and this function has been seen both ways. The
 * previous inline form was an unchecked cast:
 *
 *     const r = (Array.isArray(data) ? data[0] : data) as { mailing_id: ... };
 *     setMailingId(r.mailing_id);
 *
 * On an EMPTY array that reads `undefined.mailing_id` and throws
 * "Cannot read properties of undefined" -- which the catch then shows the user
 * as a toast. A person who pressed Queue is told about a JavaScript property
 * access. Worse, a row whose mailing_id is missing would set `null` and leave
 * the Remove button pointing at nothing.
 *
 * So the shape is checked, and a violation says which value was wrong.
 */
export function readPlanResult(data: unknown): PlannedCard {
  const row = Array.isArray(data) ? data[0] : data;
  if (row === null || row === undefined) {
    throw new Error("plan_one_door returned no row — the card was not queued.");
  }
  const id = (row as { mailing_id?: unknown }).mailing_id;
  if (typeof id !== "number" || !Number.isFinite(id)) {
    throw new Error(
      `plan_one_door returned no usable mailing_id (got ${JSON.stringify(id)}).`,
    );
  }
  // `already` is advisory -- it only picks the wording -- so a missing value
  // reads as false rather than failing a write that plainly succeeded.
  return {
    mailingId: id,
    already: (row as { already?: unknown }).already === true,
  };
}

/** Queue one card. Throws with the function's own message on refusal. */
export async function planOneDoor(
  rpc: PlanOneRpc,
  addressId: number,
  touch: string,
): Promise<PlannedCard> {
  const { data, error } = await rpc({
    p_address_id: addressId,
    p_touch: touch,
    p_vendor: null,
  });
  // plan_one_door raises for the holdout, an opted-out door and a bad touch.
  if (error) throw new Error(error.message);
  return readPlanResult(data);
}

/** Discard one queued card. */
export async function unplanOne(
  rpc: UnplanOneRpc,
  mailingId: number,
): Promise<void> {
  const { error } = await rpc({ p_mailing_id: mailingId });
  if (error) throw new Error(error.message);
}

/**
 * What to say after a successful queue.
 *
 * The `already` wording carries real information -- it is the difference
 * between "we made you a card" and "you already had one, and we did not make a
 * second" -- so it is tested rather than left inline.
 */
export function queuedMessage(
  card: PlannedCard,
  touch: string,
  mailTo: string | null | undefined,
): string {
  const who = mailTo ?? "that door";
  return card.already
    ? `${who} already had ${touch} queued — reusing it rather than making a second card.`
    : `Queued ${touch} for ${who}.`;
}
